import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  getProject, 
  updateProject, 
  deleteProject as apiDeleteProject,
  getCompetitionAnalysis,
  Project,
  Location
} from '../../services/projects';
import { Competitor, CreateCompetitorData } from '../../services/competitors';
import { CompetitionAnalysis, SimpleCompetitor, convertToFullCompetitor } from '../../types/competitors';
import { ApiError, ApiErrorType } from '../../services/api';
import ShareProjectModal from '../../components/ShareProjectModal';
import { TabType } from './hooks';

// Import components
import ProjectHeader from './components/ProjectHeader';
import ProjectTabs from './components/ProjectTabs';
import OverviewTab from './components/OverviewTab';
import PropertyDetailsTab from './components/PropertyDetailsTab';
import CompetitionTab from './components/CompetitionTab';
import MembersTab from './components/MembersTab';
import ReportsTab from './components/ReportsTab';
import BusinessIntelligenceTab from './components/BusinessIntelligence';

const ProjectDetailsPage: React.FC = () => {
  // Extract projectId from URL params
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Project data state
  const [project, setProject] = useState<Project | null>(null);
  const [competitionAnalysis, setCompetitionAnalysis] = useState<CompetitionAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Editing states
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationValues, setLocationValues] = useState<Location>({
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  
  const [editingSpecs, setEditingSpecs] = useState(false);
  const [propertyValues, setPropertyValues] = useState({
    squareFootage: '',
    ceilingHeight: ''
  });
  
  const [editingDealTerms, setEditingDealTerms] = useState(false);
  const [dealTermsValue, setDealTermsValue] = useState('');
  
  const [addingCompetitor, setAddingCompetitor] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState<Omit<CreateCompetitorData, 'project_id'>>({
    name: '',
    address: {
      formatted_address: ''
    },
    competition_type: 'Potential',
    distance_miles: 0,
    threat_rating: 0
  });
  
  const [competitors, setCompetitors] = useState<Competitor[]>([]);

  // Initialize form values when project data is loaded
  useEffect(() => {
    if (project) {
      // Initialize description
      setDescriptionValue(project.description || '');
      
      // Initialize location values
      if (project.location && typeof project.location !== 'string') {
        setLocationValues({
          address: project.location.address || '',
          city: project.location.city || '',
          state: project.location.state || '',
          zipCode: project.location.zipCode || ''
        });
      }
      
      // Initialize property details
      if (project.propertyDetails) {
        setPropertyValues({
          squareFootage: project.propertyDetails.squareFootage?.toString() || '',
          ceilingHeight: project.propertyDetails.ceilingHeight?.toString() || ''
        });
      }
      
      // Initialize deal terms
      setDealTermsValue(project.dealTerms || '');
      
      // Initialize competitors
      if (competitionAnalysis && competitionAnalysis.competitors) {
        // Convert SimpleCompetitor[] to FullCompetitor[] using our helper
        const fullCompetitors = competitionAnalysis.competitors.map(comp => 
          convertToFullCompetitor(comp, projectId || '')
        );
        setCompetitors(fullCompetitors);
      }
    }
  }, [project, competitionAnalysis]);

  // Fetch project data
  useEffect(() => {
    if (!projectId) return;

    console.log('ProjectDetailsPage: Fetching project data for ID:', projectId);
    console.log('Current URL path:', window.location.pathname);

    const fetchProjectData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Making API request to fetch project:', projectId);
        const projectData = await getProject(projectId);
        console.log('Project data received:', projectData);
        setProject(projectData);
        
        // Try to fetch competition analysis if available
        try {
          console.log('Attempting to fetch competition analysis for project:', projectId);
          const analysisData = await getCompetitionAnalysis(projectId);
          console.log('Competition analysis data received:', analysisData);
          setCompetitionAnalysis(analysisData);
        } catch (err) {
          console.log('No competition analysis available yet:', err);
          // This is expected for new projects, so we don't set the error state
          // Just set competition analysis to null
          setCompetitionAnalysis(null);
        }
      } catch (err) {
        console.error('Error fetching project details:', err);
        let errorMessage = 'Failed to load project details. Please try again.';
        
        if (err instanceof ApiError) {
          switch (err.type) {
            case ApiErrorType.UNAUTHORIZED:
              errorMessage = 'Your session has expired. Please log in again.';
              break;
            case ApiErrorType.NOT_FOUND:
              errorMessage = 'Project not found. It may have been deleted.';
              break;
            case ApiErrorType.SERVER_ERROR:
              errorMessage = 'Server error. Please try again later.';
              break;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  // Handler functions for saving edited values
  const handleSaveDescription = async () => {
    if (!projectId || !project) return;
    
    setIsLoading(true);
    try {
      // Make actual API call to update the project
      console.log('Updating project description:', descriptionValue);
      
      const updatedProject = await updateProject(projectId, { 
        description: descriptionValue 
      });
      
      setProject(updatedProject);
      setEditingDescription(false);
    } catch (err) {
      console.error('Error updating description:', err);
      let errorMessage = 'Failed to update description. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NOT_FOUND:
            errorMessage = 'Project not found. It may have been deleted.';
            break;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveLocation = async () => {
    if (!projectId || !project) return;
    
    setIsLoading(true);
    try {
      // In a real implementation, this would update the backend
      // For now, just update the local state
      const updatedProject = {
        ...project,
        location: locationValues
      };
      
      // Mock API call
      console.log('Updating project location:', locationValues);
      
      setProject(updatedProject);
      setEditingLocation(false);
    } catch (err) {
      console.error('Error updating location:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSavePropertySpecs = async () => {
    if (!projectId || !project) return;
    
    setIsLoading(true);
    try {
      // In a real implementation, this would update the backend
      // For now, just update the local state
      const updatedProject = {
        ...project,
        propertyDetails: {
          squareFootage: propertyValues.squareFootage ? parseFloat(propertyValues.squareFootage) : undefined,
          ceilingHeight: propertyValues.ceilingHeight ? parseFloat(propertyValues.ceilingHeight) : undefined
        }
      };
      
      // Mock API call
      console.log('Updating property specs:', propertyValues);
      
      setProject(updatedProject);
      setEditingSpecs(false);
    } catch (err) {
      console.error('Error updating property specs:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveDealTerms = async () => {
    if (!projectId || !project) return;
    
    setIsLoading(true);
    try {
      // In a real implementation, this would update the backend
      // For now, just update the local state
      const updatedProject = {
        ...project,
        dealTerms: dealTermsValue
      };
      
      // Mock API call
      console.log('Updating deal terms:', dealTermsValue);
      
      setProject(updatedProject);
      setEditingDealTerms(false);
    } catch (err) {
      console.error('Error updating deal terms:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddCompetitor = async () => {
    if (!projectId || !project) return;
    
    // Validate competitor data
    if (!newCompetitor.name || !newCompetitor.address?.formatted_address || 
        (newCompetitor.distance_miles !== undefined && newCompetitor.distance_miles < 0) || 
        (newCompetitor.threat_rating !== undefined && (newCompetitor.threat_rating < 0 || newCompetitor.threat_rating > 5)) ||
        !newCompetitor.competition_type) {
      alert('Please fill in all competitor fields with valid values.');
      return;
    }
    
    setIsLoading(true);
    try {
      // In a real implementation, this would update the backend
      // For now, just update the local state
      const competitorWithId = {
        ...newCompetitor,
        id: `temp-${Date.now()}`,
        project_id: projectId,
        created_at: new Date().toISOString()
      };
      
      // Mock API call
      console.log('Adding competitor:', competitorWithId);
      
      // Cast to Competitor[] as we're working with two different competitor types
      const updatedCompetitors = [...competitors, competitorWithId] as unknown as Competitor[];
      setCompetitors(updatedCompetitors);
      
      setAddingCompetitor(false);
      setNewCompetitor({
        name: '',
        address: { formatted_address: '' },
        competition_type: 'Potential',
        distance_miles: 0,
        threat_rating: 0
      });
    } catch (err) {
      console.error('Error adding competitor:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add required handler methods for CompetitionTab
  const handleUpdateCompetitor = async (id: string, data: Partial<Competitor>) => {
    setIsLoading(true);
    try {
      // Find competitor in the list
      const index = competitors.findIndex(c => c.id === id);
      if (index === -1) return;
      
      // Create updated competitor
      const updatedCompetitor = { ...competitors[index], ...data };
      
      // Update list
      const newCompetitors = [...competitors];
      newCompetitors[index] = updatedCompetitor;
      setCompetitors(newCompetitors as unknown as Competitor[]);
      
      console.log('Updated competitor:', updatedCompetitor);
    } catch (err) {
      console.error(`Error updating competitor ${id}:`, err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteCompetitor = async (id: string) => {
    setIsLoading(true);
    try {
      // Filter out the competitor with the given id
      const filteredCompetitors = competitors.filter(c => c.id !== id);
      setCompetitors(filteredCompetitors as unknown as Competitor[]);
      
      console.log('Deleted competitor:', id);
    } catch (err) {
      console.error(`Error deleting competitor ${id}:`, err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleRunAnalysis = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real implementation, this would call an API to run the analysis
      // For now, just update the status locally
      if (project) {
        const updatedProject = {
          ...project,
          status: 'New' as const
        };
        
        await updateProject(projectId, { status: 'New' });
        setProject(updatedProject);
      }
    } catch (err) {
      console.error('Error running analysis:', err);
      let errorMessage = 'Failed to run analysis. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NOT_FOUND:
            errorMessage = 'Project not found. It may have been deleted.';
            break;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;
    
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await apiDeleteProject(projectId);
      // Navigate back to the welcome page after successful deletion
      navigate('/');
    } catch (err) {
      console.error('Error deleting project:', err);
      let errorMessage = 'Failed to delete project. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NOT_FOUND:
            // If the project is not found, we can still navigate back
            navigate('/');
            return;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user can share a project
  const canShareProject = (): boolean => {
    // We want the Member Management button to always be visible
    // regardless of whether members have been added or not
    return true;
  };

const handleMemberManagement = () => {
  setShareModalOpen(true);
};

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
  };

  const handleShareSuccess = async () => {
    // Refresh project data after successful share
    if (projectId) {
      setIsLoading(true);
      try {
        const updatedProject = await getProject(projectId);
        setProject(updatedProject);
      } catch (err) {
        console.error('Error refreshing project:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading && !project) {
    return (
      <div className="project-details-page" style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading project details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="project-details-page" style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ color: 'red', padding: '1rem', backgroundColor: '#fff', borderRadius: '4px' }}>
            {error}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => navigate('/')}>Back to Projects</button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-details-page" style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Project not found.</p>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => navigate('/')}>Back to Projects</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-page" style={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Project Header */}
        <ProjectHeader 
          project={project} 
          onDeleteProject={handleDeleteProject} 
        />

        {/* Tabs */}
        <ProjectTabs 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />

        {/* Tab Content */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab 
              project={project}
              competitionAnalysis={competitionAnalysis}
              editingDescription={editingDescription}
              descriptionValue={descriptionValue}
              setEditingDescription={setEditingDescription}
              setDescriptionValue={setDescriptionValue}
              handleSaveDescription={handleSaveDescription}
              handleRunAnalysis={handleRunAnalysis}
              onViewReports={() => setActiveTab('reports')}
            />
          )}
          
          {/* Property Details Tab */}
          {activeTab === 'property' && (
            <PropertyDetailsTab 
              project={project}
              editingSpecs={editingSpecs}
              propertyValues={propertyValues}
              editingDealTerms={editingDealTerms}
              dealTermsValue={dealTermsValue}
              setEditingSpecs={setEditingSpecs}
              setPropertyValues={setPropertyValues}
              setEditingDealTerms={setEditingDealTerms}
              setDealTermsValue={setDealTermsValue}
              handleSavePropertySpecs={handleSavePropertySpecs}
              handleSaveDealTerms={handleSaveDealTerms}
              formatFileSize={formatFileSize}
            />
          )}
          
          {/* Competition Tab */}
          {activeTab === 'competition' && (
            <CompetitionTab 
              competitors={competitors}
              addingCompetitor={addingCompetitor}
              newCompetitor={newCompetitor}
              setAddingCompetitor={setAddingCompetitor}
              setNewCompetitor={setNewCompetitor}
              handleAddCompetitor={handleAddCompetitor}
              handleUpdateCompetitor={handleUpdateCompetitor}
              handleDeleteCompetitor={handleDeleteCompetitor}
            />
          )}

          {/* Business Intelligence Tab */}
          {activeTab === 'business-intelligence' && (
            <BusinessIntelligenceTab project={project} />
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <MembersTab 
              project={project}
              canShareProject={canShareProject()}
              onShareProject={handleMemberManagement}
              currentUserEmail={user?.email}
            />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <ReportsTab 
              project={project}
              competitionAnalysis={competitionAnalysis}
              isLoading={isLoading}
              handleRunAnalysis={handleRunAnalysis}
            />
          )}
        </div>
      </div>
      
      {/* Share Project Modal */}
      {shareModalOpen && (
        <ShareProjectModal
          projectId={projectId || ''}
          projectName={project.name}
          onClose={handleCloseShareModal}
          onSuccess={handleShareSuccess}
        />
      )}
    </div>
  );
};

export default ProjectDetailsPage;
